import { MessageSquareText, UserRound } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import ContextLibrary from '../components/ContextLibrary';
import WaveIcon from '../components/WaveIcon';
import { defaultConfig, interviewTypes, roles } from '../data';
import { useAuth } from '../lib/auth';
import { trackJourney } from '../lib/journey';
import type { InterviewConfig, PracticeMode } from '../types';

const modes = [
  { id: 'text' as const, title: 'Text review', detail: 'Develop the reasoning before saying it aloud', icon: MessageSquareText, tone: 'sand' },
  { id: 'voice' as const, title: 'Voice conversation', detail: 'Answer aloud with adaptive follow-ups', icon: WaveIcon, tone: 'blue' },
  { id: 'avatar' as const, title: 'Interview mode', detail: 'Add a visible interviewer for immersion', icon: UserRound, tone: 'coral' },
];

export default function NewPractice() {
  const [, navigate] = useLocation();
  const { getToken } = useAuth();
  const viewedEventId = useRef(crypto.randomUUID());
  const searchParams = new URLSearchParams(useSearch());
  const [config, setConfig] = useState<InterviewConfig>({
    ...defaultConfig,
    role: searchParams.get('role') || defaultConfig.role,
    interviewType: searchParams.get('interviewType') || defaultConfig.interviewType,
    contextIds: [],
  });

  useEffect(() => {
    if (searchParams.get('intent') !== 'upload') return;
    window.setTimeout(() => document.getElementById('context-library')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  }, []);

  useEffect(() => {
    void trackJourney(getToken, 'practice_setup_viewed', { clientEventId: viewedEventId.current });
  }, [getToken]);

  function start(mode: PracticeMode) {
    void trackJourney(getToken, 'practice_launched', {
      properties: { mode, difficulty: config.difficulty, interviewType: config.interviewType, contextCount: config.contextIds.length },
    });
    const query = new URLSearchParams({
      role: config.role,
      interviewType: config.interviewType,
      difficulty: config.difficulty,
      mode,
    });
    if (config.contextIds.length) query.set('contextIds', config.contextIds.join(','));
    navigate(`/practice?${query.toString()}`);
  }

  return (
    <div className="new-practice-page">
      <header className="practice-setup-heading">
        <h1>New practice</h1>
      </header>

      <section className="panel practice-builder">
        <div className="practice-builder-grid">
          <label className="practice-focus"><span>What are you preparing for?</span><select value={config.interviewType} onChange={(event) => setConfig({ ...config, interviewType: event.target.value })}>{interviewTypes.map((type) => <option key={type}>{type}</option>)}</select></label>
          <label><span>Difficulty</span><select value={config.difficulty} onChange={(event) => setConfig({ ...config, difficulty: event.target.value as InterviewConfig['difficulty'] })}><option value="warm-up">Warm-up</option><option value="standard">Standard</option><option value="challenging">Challenging</option></select></label>
          <label className="practice-role"><span>Target role and company</span><input list="target-roles" value={config.role} onChange={(event) => setConfig({ ...config, role: event.target.value })} /><datalist id="target-roles">{roles.map((role) => <option key={role} value={role} />)}</datalist></label>
        </div>
        <ContextLibrary
          embedded
          initialRepositoryUrl={searchParams.get('repoUrl') || ''}
          selectedIds={config.contextIds}
          onSelectedIdsChange={(contextIds) => setConfig({ ...config, contextIds })}
        />
      </section>

      <section className="practice-launch">
        <div className="section-heading"><h2>How do you want to practice?</h2></div>
        <div className="mode-grid">
          {modes.map(({ id, title, detail, icon: Icon, tone }) => (
            <button key={id} className="mode-card" onClick={() => start(id)}>
              <span className={`mode-icon ${tone}`}><Icon size={22} /></span>
              <span><strong>{title}</strong><small>{detail}</small></span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
